import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addEditPortalCredential } from "../../../../api/postCall/addEditPortalCredential";
import { CredentialWithId } from "../../../../pages/credentialsConfigration/types/credentialTypes";
import { fetchCredentialConfig } from "../../../../redux/slice/credentialConfigSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { ToastMessages } from "../../../toasts/toastMessages";
import { buildCredentialPayload } from "../utils/buildCredentialPayload";
import { CredentialFormData } from "./useCredentialForm";

interface UseCredentialSaveProps {
  formData: CredentialFormData;
  hasErrors: boolean;
  markAllTouched: () => void;
  getPasswordForSubmission: () => Promise<string>;
  getSecretKeyForSubmission: () => Promise<string>;
  isEditMode: boolean;
  credentialData: CredentialWithId | null;
  clientId?: string;
  client_Name?: string;
  onClose: () => void;
}

/**
 * Encapsulates the Save side of the modal: encrypts the sensitive fields,
 * assembles the request via `buildCredentialPayload`, POSTs to the
 * credentials endpoint, refreshes the Redux slice on success, and
 * surfaces toasts on success/error.
 *
 * If the form is invalid (`hasErrors`), `handleSave` marks every invalid
 * field touched (so all errors become visible at once) and aborts before
 * touching the network — the parent Modal's `disableSave` prop should
 * already prevent this in practice but the guard is defensive.
 */
export const useCredentialSave = ({
  formData,
  hasErrors,
  markAllTouched,
  getPasswordForSubmission,
  getSecretKeyForSubmission,
  isEditMode,
  credentialData,
  clientId,
  client_Name,
  onClose,
}: UseCredentialSaveProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const handleSave = async () => {
    if (hasErrors) {
      markAllTouched();
      return;
    }
    setIsSaving(true);
    try {
      const passwordToSend = await getPasswordForSubmission();
      const secretKeyToSend = await getSecretKeyForSubmission();

      const requestData = buildCredentialPayload({
        formData,
        isEditMode,
        credentialData,
        clientId,
        client_Name,
        userEmail: user?.email,
        passwordToSend,
        secretKeyToSend,
      });

      const response = await addEditPortalCredential(requestData);

      if (response.status === "success") {
        toast.success(ToastMessages.CREDENTIAL_SAVE_SUCCESS);
        setIsSaving(false);
        dispatch(fetchCredentialConfig());
        onClose();
      } else {
        toast.error(ToastMessages.CREDENTIAL_SAVE_ERROR);
      }
    } catch (error) {
      console.error("Error saving credential:", error);
      toast.error(ToastMessages.CREDENTIAL_SAVE_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, handleSave };
};
